"use client"

import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle, DialogTrigger } from "../ui/dialog";
import { useCallback, useEffect, useMemo, useState } from "react";
import { deletionremovedb, deletionremovedirecotry, deletionremoverelation } from "@/services/deletion.service";
import { DeleteView } from "@/app/deployment/delete/page";
import { DeletionStatus } from "@/constants/status";

interface Props {
    domain: DeleteView,
    updateData: () => void
}

export default function DeleteDomainModal(props: Props) {
    const [open, setOpen] = useState(false)
    const [deletionStatus, setDeletionStatus] = useState(DeletionStatus.standBy);
    const [failedMessage, setFailedMessage] = useState("");
    const [currentStep, setCurrentStep] = useState(-1);
    const [deletionDetails, setDeletionDetails] = useState<string[]>([]);

    const steps = useMemo(() => [
        {
            title: "Deleting Relation",
            expectedReturn: "Relation Deleted",
            functionToRun: deletionremoverelation,
            data: []
        },
        {
            title: "Delete FTP Directory",
            expectedReturn: "FTP Directory Deleted",
            functionToRun: deletionremovedirecotry,
            data: []
        },
        {
            title: "Removing DB",
            expectedReturn: "Domain removal completed",
            functionToRun: deletionremovedb,
            data: []
        },
        {
            title: "Domain Removal Completed!",
        },
    ], []);

    const handleSuccess = useCallback(() => {
        setDeletionStatus(DeletionStatus.completed);
        props.updateData()
        setOpen(false);
    }, [props]);

    const formatFormData = useCallback(
        () => {
          const formData = new FormData();
          formData.append("domainID", deletionDetails[0]);
          formData.append("companyID", deletionDetails[1]);
          return formData;
        },
        [deletionDetails]
    );

    const nextStep = useCallback(() => {
        setCurrentStep((index) => index + 1);
    }, []);

    const startStep = useCallback(async () => {
        if(currentStep < steps.length - 1){
            if(steps[currentStep].functionToRun != undefined){
                const result = await steps[currentStep].functionToRun(
                    formatFormData()
                );
            
                if(result == steps[currentStep].expectedReturn){
                    nextStep();
                } else {
                    console.log(result);
                    setDeletionStatus(DeletionStatus.failed);
                    setFailedMessage("Deletion failed. Check console.log for more information.<br>Otherwise, here: ".concat(result));
                    return;
                }
            } else {
                nextStep();
            }
        } else {
            handleSuccess();
        }
    }, [currentStep, handleSuccess, steps, formatFormData, nextStep]);

    async function handleOnSubmit(domainID: string, companyID: string){
        setDeletionStatus(DeletionStatus.onGoing);
        setDeletionDetails([domainID, companyID]);

        if (currentStep < 0) {
            setCurrentStep((index) => index + 1);
        }

        return;
    }

    useEffect(() => {
        if (currentStep > -1) {
            startStep();
        }
    }, [currentStep, startStep]);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="cursor-pointer">Delete Domain</Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
                <DialogTitle>Delete Domain</DialogTitle>
                <DialogDescription>
                    You sure you want to delete: {props.domain.Name}?
                </DialogDescription>
                <DialogFooter>
                    <div className="grid grid-cols-6 gap-4">
                        <div className="col-start-1 col-end-7 rounded-md border-black">
                            <Button className="cursor-pointer mb-3" onClick={(_e) => handleOnSubmit(props.domain.ID, props.domain.CompanyID.toString())}>Delete</Button>
                            {deletionStatus == DeletionStatus.onGoing && (
                                <div className="flex flex-col w-full max-w-2xl  gap-y-4">
                                    <p>Deleting...</p>
                                </div>
                            )}
                            {deletionStatus == DeletionStatus.completed && (
                                <div className="flex flex-col w-full max-w-2xl  gap-y-4">
                                    <p>Deletion complete! Will refresh this page momentarily</p>
                                </div>
                            )}
                            {deletionStatus == DeletionStatus.failed && (
                                <div className="flex flex-col w-full max-w-2xl  gap-y-4">
                                    <p>{failedMessage}</p>
                                </div>
                            )}

                            {currentStep > -1 && (
                                <p>{steps[currentStep].title}</p>
                            )}
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}